import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";

import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  useMediaQuery,
  Dialog,
  DialogContent,
  IconButton
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";

import "./NoticiasCarouselSlide.css";
import "./NoticiasModal.css";

/*
 * Convierte "2026-07-06" en "06 JUL 2026".
 */
function formatearFecha(fecha) {
  if (!fecha) {
    return "";
  }

  const fechaParseada = new Date(fecha + "T00:00:00");

  return new Intl.DateTimeFormat("es-UY", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  })
    .format(fechaParseada)
    .replace(".", "")
    .toUpperCase();
}

/*
 * Convierte categorías de BD en clases CSS:
 *
 * "EDUCACIÓN"    -> categoria-educacion
 * "CONSERVACIÓN" -> categoria-conservacion
 * "ALERTA"       -> categoria-alerta
 */
function obtenerClaseCategoria(categoria) {
  if (!categoria) {
    return "";
  }

  return (
    "categoria-" +
    categoria
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
  );
}

/*
 * Obtiene el Data URL/Base64 de la imagen.
 *
 * Backend:
 * GET /api/noticias/foto/{id}
 *
 * Archivo físico:
 * /home/karumbe/imagenes/noticias/{id}
 */
async function cargarImagenNoticia(idNoticia, signal) {
    const respuestaFoto = await fetch(
      "/api/noticias/foto/" + idNoticia,
      {
        signal: signal
      }
    );

    async function cargarPdfNoticia(idNoticia) {
    const respuestaPdf = await fetch(
      "/api/noticias/pdf/" + idNoticia
    );

    /*
     * Una noticia sin PDF no debería romper el popup.
     */
    if (!respuestaPdf.ok) {
      console.warn(
        "No se pudo cargar el PDF de la noticia con ID:",
        idNoticia
      );

      return "";
    }

    return respuestaPdf.text();
  }

  function obtenerNombrePdf(noticia) {
    if (!noticia || !noticia.titulo) {
      return "noticia.pdf";
    }

    const nombre = noticia.titulo
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    return nombre + ".pdf";
  }

  /*
   * Una noticia sin imagen no debería impedir que carguen
   * las otras noticias. Devuelve vacío y CardMedia queda sin foto.
   */
  if (!respuestaFoto.ok) {
    console.warn(
      "No se pudo cargar la imagen de la noticia con ID:",
      idNoticia
    );

    return "";
  }

  return respuestaFoto.text();
}
/*
 * Obtiene el Data URL/Base64 del PDF.
 *
 * Backend:
 * GET /api/noticias/pdf/{id}
 *
 * Archivo físico:
 * /home/karumbe/archivos/noticias/{id}
 */
async function cargarPdfNoticia(idNoticia, signal) {
  const respuestaPdf = await fetch(
    "/api/noticias/pdf/" + idNoticia,
    {
      signal: signal
    }
  );

  /*
   * Una noticia sin PDF no debería romper el popup.
   */
  if (!respuestaPdf.ok) {
    console.warn(
      "No se pudo cargar el PDF de la noticia con ID:",
      idNoticia
    );

    return "";
  }

  return respuestaPdf.text();
}

/*
 * Arma un nombre de archivo prolijo para descargar el PDF.
 */
function obtenerNombrePdf(noticia) {
  if (!noticia || !noticia.titulo) {
    return "noticia.pdf";
  }

  const nombre = noticia.titulo
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return nombre + ".pdf";
}

/*
 * Calcula automáticamente cuántas líneas de resumen entran
 * en la tarjeta según el espacio disponible.
 */
function ResumenAjustable({ texto }) {
  const espacioRef = useRef(null);
  const textoRef = useRef(null);
  const [lineas, setLineas] = useState(1);

  useLayoutEffect(() => {
    const calcularLineas = () => {
      const espacio = espacioRef.current;
      const contenido = textoRef.current;

      if (!espacio || !contenido) {
        return;
      }

      const estilos = window.getComputedStyle(contenido);
      const altoLinea = parseFloat(estilos.lineHeight);

      if (!altoLinea || altoLinea <= 0) {
        return;
      }

      const altoDisponible = espacio.clientHeight;

      const nuevasLineas = Math.max(
        1,
        Math.floor(altoDisponible / altoLinea)
      );

      setLineas((lineasActuales) =>
        lineasActuales === nuevasLineas
          ? lineasActuales
          : nuevasLineas
      );
    };

    const frame = window.requestAnimationFrame(calcularLineas);

    /*
     * Espera a que el carrusel se haya acomodado.
     */
    const tiempo = window.setTimeout(calcularLineas, 150);

    let observador = null;

    if (typeof ResizeObserver !== "undefined" && espacioRef.current) {
      observador = new ResizeObserver(calcularLineas);
      observador.observe(espacioRef.current);
    }

    window.addEventListener("resize", calcularLineas);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(tiempo);
      window.removeEventListener("resize", calcularLineas);

      if (observador) {
        observador.disconnect();
      }
    };
  }, []);

  return (
    <div
      ref={espacioRef}
      className="news-carousel-summary-space"
    >
      <Typography
        ref={textoRef}
        className="news-carousel-card-summary"
        style={{
          WebkitLineClamp: lineas
        }}
      >
        {texto}
      </Typography>
    </div>
  );
}

function NoticiasCarouselSlide() {
  const [noticias, setNoticias] = useState([]);
  const [cargandoNoticias, setCargandoNoticias] = useState(true);
  const [errorNoticias, setErrorNoticias] = useState(false);
  const [noticiaAbierta, setNoticiaAbierta] = useState(null);
  const [pdfNoticiaAbierta, setPdfNoticiaAbierta] = useState("");
  const [cargandoPdfNoticia, setCargandoPdfNoticia] = useState(false);

  const esCelular = useMediaQuery("(max-width:1000px)");

  useEffect(() => {
    const controlador = new AbortController();
    let componenteMontado = true;

    const cargarNoticias = async () => {
      try {
        setCargandoNoticias(true);
        setErrorNoticias(false);

        /*
         * Backend:
         * GET /api/noticias/ultimas
         *
         * Devuelve las tres noticias con mayor ID.
         */
        const urlNoticias = "/api/noticias/ultimas";

console.group("Noticias - prueba de endpoint");
console.log("Origen actual:", window.location.origin);
console.log("URL solicitada:", urlNoticias);

const respuestaNoticias = await fetch(urlNoticias, {
  signal: controlador.signal
});

console.log("URL final respondida:", respuestaNoticias.url);
console.log("Estado HTTP:", respuestaNoticias.status);
console.log("Estado OK:", respuestaNoticias.ok);
console.log(
  "Content-Type:",
  respuestaNoticias.headers.get("content-type")
);

/*
 * Leemos como texto primero para poder ver si devuelve JSON,
 * HTML de Apache, un 404 o algún mensaje de Spring.
 */
const cuerpoRespuesta = await respuestaNoticias.text();

console.log("Cuerpo de respuesta:", cuerpoRespuesta);
console.groupEnd();

if (!respuestaNoticias.ok) {
  throw new Error(
    "No se pudieron cargar las noticias. HTTP " +
    respuestaNoticias.status
  );
}

let datosNoticias;

try {
  datosNoticias = JSON.parse(cuerpoRespuesta);
} catch (errorJson) {
  throw new Error(
    "La respuesta no es JSON válido. Revisá la consola."
  );
}

console.log("Noticias convertidas desde JSON:", datosNoticias);

        /*
         * Para cada noticia, carga su foto desde:
         * /api/noticias/foto/{id}
         *
         * Promise.all permite hacer las tres consultas en paralelo.
         */
        const noticiasNormalizadas = await Promise.all(
          datosNoticias.map(async (noticia) => {
            let imagen = "";

            try {
              imagen = await cargarImagenNoticia(
                noticia.id,
                controlador.signal
              );
            } catch (errorFoto) {
              /*
               * AbortController dispara AbortError cuando el componente
               * se desmonta: en ese caso no registramos error.
               */
              if (errorFoto.name !== "AbortError") {
                console.error(
                  "Error cargando imagen de noticia:",
                  noticia.id,
                  errorFoto
                );
              }
            }

            return {
              id: noticia.id,
              categoria: noticia.categoria,
              fecha: formatearFecha(noticia.fechaPublicacion),
              titulo: noticia.titulo,
              resumen: noticia.resumen,
              contenido: noticia.contenido,
              imagen: imagen,
              claseCategoria: obtenerClaseCategoria(
                noticia.categoria
              )
            };
          })
        );

        if (componenteMontado) {
          setNoticias(noticiasNormalizadas);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error cargando noticias:", error);
    console.error("Mensaje:", error.message);

          if (componenteMontado) {
            setErrorNoticias(true);
          }
        }
      } finally {
        if (componenteMontado) {
          setCargandoNoticias(false);
        }
      }
    };

    cargarNoticias();

    return () => {
      componenteMontado = false;
      controlador.abort();
    };
  }, []);

  const abrirNoticia = async (evento, noticia) => {
    /*
     * Evita que el toque llegue al carrusel.
     */
    evento.preventDefault();
    evento.stopPropagation();

    setNoticiaAbierta(noticia);
    setPdfNoticiaAbierta("");
    setCargandoPdfNoticia(true);

    try {
      const pdf = await cargarPdfNoticia(noticia.id);
      setPdfNoticiaAbierta(pdf);
    } catch (errorPdf) {
      console.error(
        "Error cargando PDF de noticia:",
        noticia.id,
        errorPdf
      );
      setPdfNoticiaAbierta("");
    } finally {
      setCargandoPdfNoticia(false);
    }
  };

  const cerrarNoticia = () => {
    setNoticiaAbierta(null);
    setPdfNoticiaAbierta("");
    setCargandoPdfNoticia(false);
  };

  /*
   * En celular se muestra solamente la última noticia.
   * El endpoint ya las devuelve id DESC, por lo que la
   * primera es la más nueva.
   */
  const noticiasVisibles = esCelular
    ? noticias.slice(0, 1)
    : noticias.slice(0, 3);

  return (
    <>
      <div className="news-carousel-slide">
        <aside className="news-carousel-intro">
          <div className="news-wave">〰</div>

          <Typography className="news-carousel-kicker">
            ACTUALIDAD
          </Typography>

          <Typography
            className="news-carousel-title"
            variant="h3"
          >
            NOTICIAS
          </Typography>

          <Typography className="news-carousel-description">
            Información actualizada sobre rescates, educación, investigación y conservación.
          </Typography>

          <Button
            className="news-all-button"
            href="/noticias"
            variant="outlined"
          >
            VER TODAS LAS NOTICIAS
          </Button>
        </aside>

        <div className="news-carousel-cards">
          {cargandoNoticias && (
            <div className="news-carousel-empty">
              Cargando noticias...
            </div>
          )}

          {!cargandoNoticias && errorNoticias && (
            <div className="news-carousel-empty">
              No fue posible cargar las noticias.
            </div>
          )}

          {!cargandoNoticias &&
            !errorNoticias &&
            noticiasVisibles.length === 0 && (
              <div className="news-carousel-empty">
                Todavía no hay noticias publicadas.
              </div>
            )}

          {!cargandoNoticias &&
            !errorNoticias &&
            noticiasVisibles.map((noticia) => (
              <Card
                className="news-carousel-card"
                key={noticia.id}
              >
                <CardMedia
                  className="news-carousel-image"
                  image={noticia.imagen}
                  title={noticia.titulo}
                />

                <CardContent className="news-carousel-card-content">
                  <div className="news-carousel-meta">
                    <Typography
                      className={
                        "news-carousel-category " +
                        noticia.claseCategoria
                      }
                    >
                      {noticia.categoria}
                    </Typography>

                    <Typography className="news-carousel-date">
                      {noticia.fecha}
                    </Typography>
                  </div>

                  <Typography
                    className="news-carousel-card-title"
                    variant="h6"
                  >
                    {noticia.titulo}
                  </Typography>

                  <ResumenAjustable texto={noticia.resumen} />

                  <button
                    type="button"
                    className="news-carousel-link"
                    onClick={(evento) =>
                      abrirNoticia(evento, noticia)
                    }
                    onTouchStart={(evento) => {
                      evento.stopPropagation();
                    }}
                  >
                    LEER MÁS <span>→</span>
                  </button>
                </CardContent>
              </Card>
            ))}
        </div>
      </div>

      <Dialog
        open={Boolean(noticiaAbierta)}
        onClose={cerrarNoticia}
        maxWidth="md"
        fullWidth
        className="news-modal"
      >
        {noticiaAbierta && (
          <DialogContent className="news-modal-content">
            <IconButton
              className="news-modal-close"
              onClick={cerrarNoticia}
              aria-label="Cerrar noticia"
            >
              <CloseIcon />
            </IconButton>

            <div className="news-modal-image-wrapper">
              <img
                src={noticiaAbierta.imagen}
                alt={noticiaAbierta.titulo}
                className="news-modal-image"
              />
            </div>

            <div className="news-modal-body">
              <div className="news-modal-meta">
                <Typography
                  className={
                    "news-carousel-category " +
                    noticiaAbierta.claseCategoria
                  }
                >
                  {noticiaAbierta.categoria}
                </Typography>

                <Typography className="news-carousel-date">
                  {noticiaAbierta.fecha}
                </Typography>
              </div>

              <Typography
                className="news-modal-title"
                variant="h4"
              >
                {noticiaAbierta.titulo}
              </Typography>

              <Typography className="news-modal-text">
                {noticiaAbierta.contenido ||
                  noticiaAbierta.resumen}
              </Typography>
            {cargandoPdfNoticia && (
              <div className="news-modal-pdf-loading">
                Buscando PDF adjunto...
              </div>
            )}

            {pdfNoticiaAbierta && (
              <a
                className="news-modal-pdf-link"
                href={pdfNoticiaAbierta}
                download={obtenerNombrePdf(noticiaAbierta)}
              >
                DESCARGAR Informe
              </a>
            )}
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}

export default NoticiasCarouselSlide;